/**
 * Every word the public site renders lives here, so copy can be edited
 * without touching components.
 *
 * Deliberately contains no metrics, client names, testimonials or team
 * biographies. Those would be invented, and an invented number on a public
 * page about financial software is the kind of claim that is hard to walk
 * back. Add them here once they are real.
 */

export const COMPANY = {
  name: 'ApexFlow',
  tagline: 'Software built end to end — for every platform.',
  intro:
    'We design and build software across web, mobile, desktop and cloud. ' +
    'Our focus is fintech and blockchain systems, where correctness is not ' +
    'optional — but we take on custom builds of every kind, and we do the ' +
    'whole job: architecture, implementation, deployment and the running of it.',
  /**
   * Must be a real, monitored mailbox. The domain already has mail hosted, so
   * this needs to exist as an address before the site is announced anywhere.
   */
  email: 'contact@apexflow.in',
  domain: 'apexflow.in',
};

export interface Service {
  title: string;
  summary: string;
  items: string[];
}

export const SERVICES: Service[] = [
  {
    title: 'Fintech & payments',
    summary:
      'Systems that move money and have to be right the first time. Double-entry ledgers, settlement and reconciliation, payment-gateway and banking integrations, subscription billing, and the compliance surfaces that come with them.',
    items: [
      'Double-entry ledgers and reconciliation',
      'Payment gateway and bank integrations',
      'Settlement, payouts and refund flows',
      'Subscription and recurring billing',
      'Audit trails and regulatory reporting',
    ],
  },
  {
    title: 'Blockchain & crypto technology',
    summary:
      'On-ramp and off-ramp flows, wallet integrations, on-chain settlement and the orchestration layers around them — including non-custodial designs where you never hold customer funds or keys.',
    items: [
      'On-ramp / off-ramp orchestration',
      'Wallet and exchange integrations',
      'Non-custodial architecture',
      'Smart contracts and on-chain settlement',
      'Transaction monitoring and webhooks',
    ],
  },
  {
    title: 'Web applications',
    summary:
      'Product interfaces and dashboards, built as fast single-page apps or server-rendered sites, with the APIs behind them designed as part of the same job rather than bolted on.',
    items: [
      'Single-page apps and dashboards',
      'REST and real-time APIs',
      'Authentication and access control',
      'Admin and internal tooling',
      'Third-party API integrations',
    ],
  },
  {
    title: 'Mobile & desktop',
    summary:
      'Native and cross-platform applications for Android, iOS, Windows, macOS and Linux — sharing one backend so behaviour does not drift between platforms.',
    items: [
      'Android and iOS applications',
      'Cross-platform builds',
      'Desktop software',
      'Offline-capable and sync-aware clients',
      'Store submission and release management',
    ],
  },
  {
    title: 'Cloud, infrastructure & DevOps',
    summary:
      'Getting it deployed and keeping it up. Containerised services on Azure, AWS or GCP, with CI/CD, managed databases, secret handling, monitoring and alerting configured as part of delivery.',
    items: [
      'Azure, AWS and GCP deployments',
      'Containers and CI/CD pipelines',
      'Managed databases and caching',
      'Secrets management and hardening',
      'Monitoring, logging and alerting',
    ],
  },
  {
    title: 'Custom & legacy work',
    summary:
      'Work that does not fit a category: automation, data pipelines, integrations between systems that were never meant to talk, and taking over software someone else wrote.',
    items: [
      'Process automation and data pipelines',
      'System-to-system integrations',
      'Taking over and stabilising existing code',
      'Migrations between platforms and hosts',
      'Technical review and consulting',
    ],
  },
];

export interface Principle {
  title: string;
  body: string;
}

/** What we actually commit to — phrased so each one is checkable. */
export const PRINCIPLES: Principle[] = [
  {
    title: 'Affordable, and quoted before we start',
    body:
      'You get a written scope and a price before any work begins. We quote per project, not per hour of meetings, and we will tell you when something you asked for is not worth building.',
  },
  {
    title: 'Everything in one place',
    body:
      'Architecture, build, deployment and maintenance from the same team. No handoff to a third party the moment the code is written, and no gap where a problem is nobody’s job.',
  },
  {
    title: 'You own it',
    body:
      'Source code, infrastructure and accounts are yours and stay in your accounts. No lock-in to a platform we control, and no dependency on us to keep the lights on.',
  },
  {
    title: 'Tested where it counts',
    body:
      'Money paths, auth and data integrity get automated tests and are designed to fail loudly rather than quietly do the wrong thing. We would rather a deploy refuse to start than run misconfigured.',
  },
];

export interface ProcessStep {
  step: string;
  title: string;
  body: string;
}

export const PROCESS: ProcessStep[] = [
  {
    step: '01',
    title: 'Scope',
    body: 'We work out what you actually need, what it will cost and how long it takes. If the answer is smaller than what you asked for, we say so.',
  },
  {
    step: '02',
    title: 'Build',
    body: 'Delivered in working increments you can see and use, not a single reveal at the end. You can change direction while it is still cheap to.',
  },
  {
    step: '03',
    title: 'Deploy',
    body: 'Into your own cloud accounts, with CI/CD, monitoring and backups configured — not left as an exercise for later.',
  },
  {
    step: '04',
    title: 'Support',
    body: 'Ongoing maintenance, fixes and changes. Or a clean handover with documentation if you would rather run it yourself.',
  },
];

export const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/services', label: 'Services', end: false },
  { to: '/approach', label: 'Approach', end: false },
  { to: '/contact', label: 'Contact', end: false },
];
